import { Pages } from '@/constants/pages';
import { useMixedPanel } from '@/hooks/useMixPanel';
import {
  Paper,
  Tab,
  TabProps,
  Tabs,
  TabsProps,
  buttonBaseClasses,
  tabClasses,
  tabsClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Link from 'next/link';

export type StyledTabsProps = TabsProps;

const StyledTabs = styled((props: StyledTabsProps) => (
  <Paper elevation={0}>
    <Tabs {...props} />
  </Paper>
))(({ theme }) => ({
  height: 40,
  minHeight: 40,
  background: theme.palette.custom.tabs.backgroundColor,
  color: theme.palette.custom.tabs.textColor,

  [`&.${tabsClasses.root}`]: {
    height: '100%',
    paddingLeft: 18.7,
    paddingRight: 18.7,
  },

  [`& .${tabsClasses.flexContainer}`]: {
    justifyContent: 'space-between',
  },

  [`& .${tabsClasses.indicator}`]: {
    height: 2,
    // marginBottom: 8,
    backgroundColor:
      theme.palette.custom.tabs.activeTabColor ||
      theme.palette.custom.global.accentColor,
  },
}));

interface StyledTabProps extends TabProps {}

const StyledTab = styled((props: StyledTabProps) => <Tab {...props} />)(
  ({ theme }) => ({
    padding: 0,
    fontWeight: 'normal',
    width: 'auto',
    [`&.${buttonBaseClasses.root}`]: {
      minWidth: 'fit-content',
      width: 'auto',
      margin: 0,
      minHeight: 40,
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
        flexGrow: 1,
      },
    },
    [`&.${tabClasses.selected}`]: {
      color:
        theme.palette.custom.tabs.activeTabColor ||
        theme.palette.custom.global.accentColor,
    },
    [`&.${tabClasses.disabled}`]: {
      color: theme.palette.custom.tabs.disabledTabColor,
    },
    color: theme.palette.custom.tabs.textColor,
    textTransform: 'none',
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
  }),
);

export type PrimaryTabItem = {
  title: string;
  href: keyof typeof Pages;
  disabled?: boolean;
  selected?: boolean;
};

export type PrimaryTabsProps = {
  items: Array<PrimaryTabItem>;
};

const PrimaryTabs = ({ items = [] }: PrimaryTabsProps) => {
  const selectedIndex = items.findIndex((i) => i.selected === true);
  const { trackPageView } = useMixedPanel();

  return (
    <StyledTabs
      value={selectedIndex != -1 ? selectedIndex : undefined}
      allowScrollButtonsMobile
    >
      {items.map((item) => {
        return (
          <StyledTab
            disabled={item.disabled}
            key={item.href}
            component={Link}
            // @ts-ignore
            href={item.href}
            label={item.title}
            onClick={() => {
              trackPageView(`${item.title} page`);
            }}
          />
        );
      })}
    </StyledTabs>
  );
};

export default PrimaryTabs;
